// AI usage hook for the topbar badge (KAN-1293).
// Reads from GET /api/v1/usage/ai?scope=mine|org. Cached for 60s — the badge
// only needs to feel "live enough" and we don't want every page-render to
// trigger a DB hit.

import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api";
import { API_BASE } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import { keys } from "./keys";

export type UsageScope = "mine" | "org";

export interface AIUsageResponse {
  tier: string;
  scope: UsageScope;
  usedCents: number;
  limitCents: number;           // -1 = unlimited
  resetAt: string;              // ISO timestamp — first of next month UTC
  mineCents: number;            // 0 when scope=org
  byAgent: Array<{
    id: string;
    label: string;
    cents: number;
  }>;
}

/**
 * Build the AI usage URL for a given scope. Exported (and unit-tested) so
 * the URL shape used by the hook stays in lockstep with the backend route.
 */
export function buildAIUsageUrl(scope: UsageScope): string {
  return `${API_BASE}/api/v1/usage/ai?scope=${scope}`;
}

/**
 * The actual fetch the hook delegates to — exported so we can unit-test the
 * apiFetch wiring without spinning up a React Query client. Mirrors the
 * pattern in use-widget-insights to avoid the double-unwrap regression.
 */
export function fetchAIUsage(scope: UsageScope): Promise<AIUsageResponse> {
  return apiFetch<AIUsageResponse>(buildAIUsageUrl(scope));
}

export interface UseAIUsageOptions {
  /**
   * Poll at 5s instead of the idle 60s. Used when the slide-over is open —
   * the user is actively watching, so the number should tick within ~5s of
   * any LLM call landing in llm_usage_events.
   */
  fast?: boolean;
  /**
   * Set false to keep the subscription but run no polling timer of its own.
   * In React Query v5 every observer owns its own refetch timer (they do NOT
   * merge to "the faster interval wins"), so when two components watch the
   * same key, exactly one of them should poll — the other reads the shared
   * cache. The topbar badge hands polling to the slide-over while it is open.
   */
  poll?: boolean;
}

export function useAIUsage(scope: UsageScope = "org", opts: UseAIUsageOptions = {}) {
  const { userContext } = useAuth();
  const orgId = userContext?.orgId;

  // KAN-1293: feels-live polling. Endpoint reads from llm_usage_events
  // (per-call audit trail). The idle pill is chrome on every page — once a
  // minute is plenty (a stale-aware focus refetch covers tab returns); the
  // open slide-over is the live view and polls at 5s.
  const interval = opts.fast ? 5_000 : 60_000;
  return useQuery({
    queryKey: keys.aiUsage.byScope(orgId, scope),
    queryFn: () => fetchAIUsage(scope),
    enabled: !!orgId,
    staleTime: interval,
    refetchInterval: (opts.poll ?? true) ? interval : false,
    // "always", not true: a stale-aware focus refetch skips when the cache is
    // younger than staleTime, so two tabs compared within a minute showed
    // different balances "at the same moment" and only reconciled on reload --
    // each tab's cache was independently fresh-enough and independently wrong.
    // Focus is exactly the moment the user is looking; one request per
    // tab-switch is the cheapest honest number.
    refetchOnWindowFocus: "always",
  });
}
